import { useEffect, useState, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import { useTimeRange } from "../../../context/TimeRangeContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const EmployeeOverallPerformance = ({ chartType = "bar" }) => {
  const chartRef = useRef(null);
  const { timeRange } = useTimeRange();
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [chartOptions, setChartOptions] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchPerformanceData = async (timeRange) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        `https://zola.technology/api/company/employees/analytics/get-all-with-taps/?filter=${timeRange}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const data = result.employees.map(
          (item) => item.filtered_taps_count || 0
        );
        const categories = result.employees.map(
          (item) => item.name || "Unknown"
        );

        // Set chart data for all types
        const commonDataset = {
          label: "Employee Performance (taps)",
          data,
          backgroundColor:
            chartType === "doughnut"
              ? [
                  "#3366FF",
                  "#66bb6a",
                  "#ff7043",
                  "#ab47bc",
                  "#ffa726",
                  "#ab47bc",
                ]
              : chartType === "bar"
              ? "#3366FF"
              : "rgba(51,102,255,0.2)",
          borderColor: "#3366FF",
          borderWidth: 2,
          tension: 0.3,
          fill: chartType === "area",
          pointRadius: 0,
        };

        setChartData({
          labels: categories,
          datasets: [commonDataset],
        });

        setChartOptions({
          responsive: true,
          maintainAspectRatio: false,
          cutout: chartType === "doughnut" ? "70%" : undefined,
          plugins: {
            legend: {
              position: chartType === "doughnut" ? "bottom" : "top",
              labels: {
                color: "#333",
              },
            },
          },
          scales:
            chartType === "doughnut"
              ? {}
              : {
                  x: {
                    ticks: {
                      color: "#8c9098",
                      font: { size: 12 },
                    },
                    grid: { display: false },
                  },
                  y: {
                    ticks: {
                      color: "#8c9098",
                      font: { size: 12 },
                      callback: (value) => value.toLocaleString(),
                    },
                    grid: { color: "#e7e7e7" },
                    title: {
                      display: true,
                      text: "Performance (in taps)",
                      color: "#8c9098",
                      font: { size: 12, weight: "bold" },
                    },
                  },
                },
        });
      } else {
        console.error("Failed to fetch employee performance data");
      }
    } catch (error) {
      console.error("Error fetching performance data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPerformanceData(timeRange);   
  }, [timeRange, chartType]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 mb-4 shadow-md">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Employee Performance Overview
      </h2>
      {loading ? (
        <p className="text-gray-700 dark:text-gray-300">Loading...</p>
      ) : (
        <div className="w-[95%] md:w-full h-[350px] lg:h-[400px]">
          {chartType === "bar" && (
            <Bar ref={chartRef} data={chartData} options={chartOptions} />
          )}
          {(chartType === "line" || chartType === "area") && (
            <Line ref={chartRef} data={chartData} options={chartOptions} />
          )}
          {chartType === "doughnut" && (
            <Doughnut ref={chartRef} data={chartData} options={chartOptions} />
          )}
        </div>
      )}
    </div>
  );
};

export default EmployeeOverallPerformance;
